import { useMemo, useState } from 'react';
import { Plug, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCategory, getCategoryColor } from '@/lib/constants/integration-categories';
import { useAllTenantsIntegrations, useIntegrationDefinitions } from '@/hooks/use-integrations';

interface TenantIntegrationsSectionProps {
  tenantId: string;
}

const COLLAPSED_COUNT = 5;

export function TenantIntegrationsSection({ tenantId }: TenantIntegrationsSectionProps) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const { data: allInstances, isLoading: instancesLoading } = useAllTenantsIntegrations();
  const { data: definitions, isLoading: definitionsLoading } = useIntegrationDefinitions();

  const tenantInstances = useMemo(() => {
    if (!allInstances) return [];
    return allInstances.filter(i => i.tenant_id === tenantId);
  }, [allInstances, tenantId]);

  const definitionMap = useMemo(() => {
    if (!definitions) return new Map<string, { name: string; category_id: string }>();
    return new Map(definitions.map(d => [d.id, { name: d.name, category_id: d.category_id }]));
  }, [definitions]);

  const isLoading = instancesLoading || definitionsLoading;
  const visible = expanded ? tenantInstances : tenantInstances.slice(0, COLLAPSED_COUNT);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Plug className="h-5 w-5" />
          Connected Integrations
          {!isLoading && (
            <Badge variant="secondary" className="ml-auto text-xs">
              {tenantInstances.length}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex flex-wrap gap-2">
            <Skeleton className="h-10 w-28 rounded-lg" />
            <Skeleton className="h-10 w-32 rounded-lg" />
            <Skeleton className="h-10 w-24 rounded-lg" />
            <Skeleton className="h-10 w-36 rounded-lg" />
          </div>
        ) : tenantInstances.length === 0 ? (
          <p className="text-sm text-muted-foreground">No integrations connected</p>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {visible.map(instance => {
                const def = definitionMap.get(instance.integration_id);
                const categoryColor = getCategoryColor(def?.category_id ?? '');
                const isItsm = def?.category_id === 'itsm';
                const badgeClass = `inline-flex items-center gap-2 rounded-lg border px-3 py-2 ${categoryColor}`;
                const inner = (
                  <>
                    <span className="text-sm font-medium">{def?.name ?? instance.integration_id}</span>
                    <span className="text-xs text-muted-foreground">
                      {def ? formatCategory(def.category_id) : ''}
                    </span>
                    {instance.deprecated && (
                      <Badge variant="destructive" className="text-[10px] px-1 py-0">Deprecated</Badge>
                    )}
                  </>
                );
                return isItsm ? (
                  <button
                    key={instance.id}
                    type="button"
                    title="Open in ITSM Config"
                    onClick={() => { void navigate({ to: '/itsm-config', search: { tenantId } }); }}
                    className={`${badgeClass} hover:ring-ring/40 cursor-pointer transition-shadow hover:ring-2`}
                  >
                    {inner}
                    <ExternalLink className="h-3 w-3 opacity-60" />
                  </button>
                ) : (
                  <div key={instance.id} className={badgeClass}>
                    {inner}
                  </div>
                );
              })}
              {tenantInstances.length > COLLAPSED_COUNT && (
                <button
                  type="button"
                  onClick={() => { setExpanded(!expanded); }}
                  className="inline-flex items-center gap-1 rounded-lg border border-dashed border-border px-3 py-2 text-sm text-muted-foreground hover:bg-muted transition-colors cursor-pointer"
                >
                  {expanded ? 'Show less' : `+${tenantInstances.length - COLLAPSED_COUNT} more`}
                  {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => { void navigate({ to: '/integrations' }); }}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            >
              View all integrations →
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
